import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtMoney, accountBreakdown, transactionBreakdown, currentYM } from '../engine.js';
import { GLOSSARY } from '../data.js';

/* ExplainModal — explains a single MoneyCell's number with breakdown,
   formula (from GLOSSARY[term]), registry references, and a copy block.

   Props:
     open        — bool
     onClose     — fired on Esc or backdrop
     value       — number being explained
     label       — optional human label of the metric
     breakdown   — optional array of {label, amount, source?, cat?, categoryKey?, accounts?}
     term        — optional GLOSSARY key
     refs        — optional array from REGISTRY (consumer sites)
     formula     — optional override formula string
     scenario    — optional scenario (needed to resolve account/transaction breakdowns)
*/

const BTN_ICON = 'bg-surface-raised text-text-muted border border-border rounded-sm w-7 h-7 cursor-pointer font-[inherit] flex items-center justify-center p-0 hover:text-accent-red hover:border-red-soft';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const BLOCK_LABEL = 'block text-[0.7rem] uppercase tracking-[0.06em] text-text-dim mb-[0.3rem]';
const DRILL_BTN = 'mt-[0.2rem] bg-transparent border border-dashed border-border-strong text-text-muted px-[0.55rem] py-[0.2rem] rounded-sm cursor-pointer font-[inherit] text-[0.72rem] hover:text-accent-gold hover:border-accent-gold';

export default function ExplainModal({
  open, onClose,
  value, label, breakdown, term, refs, formula, scenario,
  zohoRef,
}) {
  const [expandedRow, setExpandedRow] = useState(null);          /* breakdown row idx */
  const [expandedAccount, setExpandedAccount] = useState(null);  /* accountId */

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /* Reset drill state when re-opening */
  useEffect(() => {
    if (!open) { setExpandedRow(null); setExpandedAccount(null); }
  }, [open]);

  if (!open) return null;

  /* v7.3 — split breakdown into regular + JE rows for distinct rendering */
  const regularBreakdown = Array.isArray(breakdown) ? breakdown.filter(r => r.cat !== 'JE') : [];
  const jeBreakdown      = Array.isArray(breakdown) ? breakdown.filter(r => r.cat === 'JE') : [];
  const total = regularBreakdown.length > 0
    ? regularBreakdown.reduce((s, r) => s + (r.amount || 0), 0)
    : value;
  const gloss = term && GLOSSARY[term];
  const effectiveFormula = formula || gloss?.formula;
  const ym = currentYM();

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/55 px-4 py-4 backdrop-blur-[4px] sm:py-8" onClick={onClose}>
      <div
        className="flex w-full min-w-0 max-w-[720px] max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-lg border border-border-strong bg-surface-el shadow-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Explain this number"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-[1.1rem] py-[0.85rem]">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-text m-0">Explain this number</h3>
            {label && (
              <p className="text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm m-0 break-words">
                {label}
              </p>
            )}
          </div>
          <button type="button" className={BTN_ICON} onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="min-h-0 overflow-y-auto overscroll-contain px-[1.1rem] pt-4 pb-[1.2rem]">
          <div className="flex flex-wrap items-baseline justify-between gap-2 px-[0.8rem] py-[0.6rem] bg-surface-raised border border-border rounded-sm mb-[0.85rem]">
            <span className="text-[0.7rem] uppercase tracking-[0.06em] text-text-dim">Value</span>
            <strong className="break-all text-[1.4rem] text-accent-gold tabular-nums">{fmtMoney(value, { decimals: 2 })}</strong>
          </div>

          {zohoRef && (
            <div className="mb-[0.85rem]">
              <span className={BLOCK_LABEL}>ZB Reference</span>
              <code className="block bg-surface-raised border border-dashed border-accent-gold px-[0.6rem] py-[0.4rem] rounded-sm font-mono text-[0.78rem] text-accent-gold">
                {Array.isArray(zohoRef) ? zohoRef.join(' · ') : zohoRef}
              </code>
            </div>
          )}

          {effectiveFormula && (
            <div className="mb-[0.85rem]">
              <span className={BLOCK_LABEL}>Formula</span>
              <code className="block bg-surface-raised border border-dashed border-border-strong px-[0.7rem] py-[0.45rem] rounded-sm text-accent-cyan text-[0.82rem] whitespace-pre-wrap">
                {effectiveFormula}
              </code>
            </div>
          )}

          {gloss && (
            <div className="mb-[0.85rem]">
              <span className={BLOCK_LABEL}>Glossary — {gloss.term}</span>
              <p className="m-0 text-text-muted text-[0.85rem] leading-[1.5]">{gloss.long || gloss.short}</p>
            </div>
          )}

          {regularBreakdown.length > 0 && (
            <div className="mb-[0.85rem]">
              <span className={BLOCK_LABEL}>Breakdown ({regularBreakdown.length} contributors · total {fmtMoney(total, { decimals: 2 })})</span>
              <ul className="list-none p-0 m-0">
                {regularBreakdown.map((r, i) => {
                  const pct = total > 0 ? (r.amount / total) : 0;
                  const inlineAccounts = Array.isArray(r.accounts) ? r.accounts : null;
                  const computedAccounts = !inlineAccounts && r.categoryKey && scenario
                    ? accountBreakdown(r.categoryKey, scenario, ym)
                    : null;
                  const accounts = inlineAccounts ?? computedAccounts;
                  const hasAccounts = accounts && accounts.length > 0;
                  const isOpen = expandedRow === i;
                  return (
                    <li key={i} className="border-b border-dashed border-border py-[0.3rem] text-[0.82rem]">
                      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-2 items-baseline">
                        <span className="min-w-0 break-words">{r.label}</span>
                        {r.source && <em className="text-[0.7rem] text-text-dim not-italic block">{r.source}</em>}
                        <span className="text-text tabular-nums font-semibold">{fmtMoney(r.amount, { decimals: 2 })}</span>
                        <span className="text-text-muted text-[0.72rem] min-w-[48px] text-right">{(pct * 100).toFixed(1)}%</span>
                      </div>
                      {hasAccounts && (
                        <button
                          type="button"
                          className={DRILL_BTN}
                          onClick={() => {
                            setExpandedRow(isOpen ? null : i);
                            setExpandedAccount(null);
                          }}
                        >
                          {isOpen ? '▾' : '▸'} {accounts.length} account{accounts.length === 1 ? '' : 's'}
                        </button>
                      )}
                      {isOpen && hasAccounts && (
                        <ul className="list-none mt-[0.4rem] mb-0 px-[0.6rem] py-[0.4rem] bg-surface-raised border border-dashed border-border rounded-sm">
                          {accounts.map((a) => {
                            const tx = expandedAccount === a.accountId
                              ? transactionBreakdown(a.accountId, scenario, ym, a.label)
                              : null;
                            const amt = a.monthly ?? a.balance ?? 0;
                            return (
                              <li key={a.accountId} className="py-[0.3rem] border-b border-dashed border-border last:border-b-0">
                                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-[0.4rem] items-baseline text-[0.78rem]">
                                  <span className="min-w-0 break-words text-text font-medium">{a.label}</span>
                                  {a.source && <em className="text-[0.7rem] text-text-dim not-italic block">{a.source}</em>}
                                  <span className="text-text tabular-nums font-semibold">{fmtMoney(amt, { decimals: 2 })}</span>
                                  <button
                                    type="button"
                                    className={`${DRILL_BTN} text-[0.68rem] px-[5px] py-px ml-[0.4rem]`}
                                    onClick={() => setExpandedAccount(expandedAccount === a.accountId ? null : a.accountId)}
                                  >
                                    {expandedAccount === a.accountId ? '▾' : '▸'} transactions
                                  </button>
                                </div>
                                {tx && (
                                  tx.loading ? (
                                    <p className="mt-[0.3rem] mb-0 px-[0.55rem] py-[0.4rem] bg-[rgba(96,165,250,0.10)] border border-dashed border-accent-blue rounded-sm text-accent-blue text-[0.74rem]">⏳ Loading ZB transactions…</p>
                                  ) : tx.hasSync && tx.transactions.length > 0 ? (
                                    <div>
                                      <ul className="list-none p-0 my-[0.4rem] max-h-[320px] overflow-y-auto bg-surface border border-border rounded-sm">
                                        {tx.transactions.map((t, j) => (
                                          <li key={t.id || j} className="grid grid-cols-[5rem_minmax(0,1fr)_1.125rem_minmax(5rem,auto)] gap-[0.4rem] px-[0.5rem] py-[0.3rem] border-b border-dashed border-border last:border-b-0 text-[0.78rem] tabular-nums items-baseline">
                                            <span className="text-text-dim font-mono">{t.date}</span>
                                            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-text" title={t.description || t.reference}>
                                              {t.payee || t.description || t.type}
                                            </span>
                                            <span className="text-center text-text-muted text-[0.88rem]" title={t.direction}>
                                              {t.direction === 'debit' ? '↑' : '↓'}
                                            </span>
                                            <span className="text-right text-text font-medium">{fmtMoney(t.amount, { decimals: 2 })}</span>
                                          </li>
                                        ))}
                                      </ul>
                                      {tx.total > 50 && (
                                        <p className={HINT}>Showing 50 of {tx.total} transactions</p>
                                      )}
                                    </div>
                                  ) : tx.hasSync && tx.transactions.length === 0 ? (
                                    <p className="mt-[0.3rem] mb-0 px-[0.55rem] py-[0.4rem] bg-[rgba(96,165,250,0.10)] border border-dashed border-accent-blue rounded-sm text-text-muted text-[0.74rem]">No ZB transactions found for this account.</p>
                                  ) : (
                                    <p className="mt-[0.3rem] mb-0 px-[0.55rem] py-[0.4rem] bg-[rgba(96,165,250,0.10)] border border-dashed border-accent-blue rounded-sm text-accent-blue text-[0.74rem]">⚡ {tx.ctaMessage}</p>
                                  )
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {jeBreakdown.length > 0 && (
            <div className="mb-[0.85rem] border-l-2 border-accent-gold pl-2">
              <span className={BLOCK_LABEL}>Adjusting Entries ({jeBreakdown.length})</span>
              <ul className="list-none p-0 m-0">
                {jeBreakdown.map((je, i) => (
                  <li key={je.jeId || `je-${i}`} className="border-b border-dashed border-border py-[0.3rem] text-[0.82rem]">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2 items-baseline">
                      <span className="min-w-0 break-words">
                        {je.jeDate ? <em className="not-italic text-accent-gold font-mono text-[0.72rem] mr-[0.3rem]">{je.jeDate}</em> : null}
                        {' '}{je.label}
                      </span>
                      {je.source && <em className="text-[0.7rem] text-text-dim not-italic block">{je.source}</em>}
                      <span className="text-text tabular-nums font-semibold">{fmtMoney(je.amount, { decimals: 2 })}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(refs) && refs.length > 0 && (
            <div className="mb-[0.85rem]">
              <span className={BLOCK_LABEL}>Used in</span>
              <ul className="list-none p-0 m-0">
                {refs.map((r, i) => (
                  <li key={i} className="py-[0.25rem] text-[0.82rem] text-text-muted">
                    <strong className="inline-block bg-surface-raised px-[6px] py-px rounded-[3px] mr-[0.4rem] text-[0.7rem] text-accent-gold tracking-[0.05em]">
                      {r.tab.toUpperCase()}
                    </strong> · {r.metric}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
